import { createContext, Suspense, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { conMovimiento, entrada, useGSAP } from '../animaciones';
import { api, creditosDisponibles, type EstadoDePaso, type Marca as TipoMarca, type Paso, type Precios, type Trabajo } from '../api';
import { Icono, type NombreDeIcono } from '../iconos';
import { iniciales, useSesion } from '../sesion';
import { supabase } from '../supabase';
import { SelectorDeIdioma, useIdioma } from '../idioma';
import { BotonDeTema } from '../tema';
import { Emblema, logoDeIdentidad } from '../componentes/emblema';

/** Lo que va después de hacer el contenido: sacarlo a las redes y ver cómo le fue. */
const SALIR: Array<{ id?: Paso; ruta: string; nombre: string; icono: NombreDeIcono }> = [
  { ruta: 'publicar', nombre: 'Publicar', icono: 'publicar' },
  { ruta: 'medicion', nombre: 'Medición', icono: 'estrella' },
  { ruta: 'historial', nombre: 'Meses anteriores', icono: 'reloj' },
];

/**
 * Los pasos de los asistentes, en el orden en que se hacen (29-09-2026, estructura del dueño): Tu marca es todo lo que
 * sabemos; Tu mes, el cronograma y los textos. La producción (diseño gráfico y video) y la publicación van aparte.
 */
export const GRUPOS: Array<{ titulo: string; pasos: Array<{ id: Paso; ruta: string; nombre: string; icono: NombreDeIcono }> }> = [
  {
    titulo: 'Tu marca',
    pasos: [
      { id: 'descubrimiento', ruta: 'negocio', nombre: 'Lo que conocemos', icono: 'marca' },
      { id: 'estrategia', ruta: 'estrategia', nombre: 'Tu estrategia', icono: 'estrategia' },
      { id: 'auditoria', ruta: 'redes', nombre: 'Tus redes hoy', icono: 'redes' },
      { id: 'estilo', ruta: 'identidad', nombre: 'Tu identidad', icono: 'identidad' },
    ],
  },
  {
    titulo: 'Tu mes',
    pasos: [
      { id: 'parrilla', ruta: 'mes', nombre: 'Cronograma', icono: 'plan' },
      { id: 'textos', ruta: 'textos', nombre: 'Textos', icono: 'textos' },
    ],
  },
];

/**
 * El menú: entradas fijas, sin acordeones (siempre mide lo mismo). «Producción» es solo un título: Diseño gráfico y
 * Video son entradas propias, sin relación entre ellas. «Publicar y medir» agrupa la programación y las métricas. Meses
 * anteriores, Tus marcas y Salir van en el menú de la cuenta.
 */
type Entrada =
  | { titulo: string }
  | { nombre: string; corto?: string; icono: NombreDeIcono; ruta?: string; grupo?: (typeof GRUPOS)[number]; rutas: string[] };

const SECCIONES: Entrada[] = [
  { nombre: 'Inicio', icono: 'inicio', ruta: 'inicio', rutas: ['inicio'] },
  { nombre: 'Tu marca', icono: 'marca', grupo: GRUPOS[0], rutas: ['marca', ...GRUPOS[0]!.pasos.map((p) => p.ruta)] },
  { nombre: 'Tu mes', icono: 'plan', grupo: GRUPOS[1], rutas: GRUPOS[1]!.pasos.map((p) => p.ruta) },
  { titulo: 'Producción' },
  { nombre: 'Diseño gráfico', icono: 'disenos', ruta: 'disenos', rutas: ['disenos'] },
  // Tu material (fotos y videos reales) vive dentro de Video (05-10-2026, pedido del dueño).
  { nombre: 'Video', icono: 'videos', ruta: 'video', rutas: ['video', 'videos', 'presentador', 'estudio', 'material'] },
  { titulo: 'Publicar y medir' },
  { nombre: 'Programación', corto: 'Publicar', icono: 'publicar', ruta: 'publicar', rutas: ['publicar'] },
  { nombre: 'Métricas', icono: 'estrella', ruta: 'medicion', rutas: ['medicion'] },
];

type Contexto = {
  marcaId: string;
  marca: TipoMarca | null;
  pasos: Partial<Record<Paso, EstadoDePaso>>;
  trabajos: Trabajo[];
  creditos: number | null;
  sinLimite: boolean;
  precios: Precios | null;
  recargar: () => Promise<void>;
  /** El mes que se está viendo en los pasos del mes (plan, textos, diseños, videos, publicar). */
  mes: string | null;
  /** Los meses que ya tienen plan. */
  meses: string[];
  cambiarMes: (m: string) => void;
};

/** Exportado para poder pintar una pantalla con datos de ejemplo al revisarla. */
export const Ctx = createContext<Contexto | null>(null);
export const useMarca = () => useContext(Ctx)!;

export function PantallaDeMarca() {
  const { id = '' } = useParams();
  const ir = useNavigate();
  const { sesion } = useSesion();
  const { t } = useIdioma();
  const [datos, setDatos] = useState<Omit<Contexto, 'marcaId' | 'recargar' | 'cambiarMes'>>({
    marca: null, pasos: {}, trabajos: [], creditos: null, sinLimite: false, precios: null, mes: null, meses: [],
  });
  // El mes elegido se recuerda por marca en este navegador.
  const clave = `igor-mes-${id}`;
  const [mesPedido, setMesPedido] = useState<string | null>(() => { try { return localStorage.getItem(clave); } catch { return null; } });
  const cambiarMes = useCallback((m: string) => {
    setMesPedido(m);
    try { localStorage.setItem(clave, m); } catch { /* sin almacenamiento, no pasa nada */ }
  }, [clave]);
  const [error, setError] = useState('');
  const lienzo = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  // Al cambiar de paso, sus cajas entran una tras otra. El inicio tiene su propia entrada, más elaborada.
  useGSAP(
    () =>
      conMovimiento(() => {
        if (pathname.endsWith('/inicio')) return;
        entrada('.tope, .tarjeta, .mini-card, .opcion-modelo, .escena, .pieza, .dis', { cada: 0.045 });
      }),
    { scope: lienzo, dependencies: [pathname, Boolean(datos.marca)] },
  );

  const recargar = useCallback(async () => {
    try {
      // Los precios los manda el motor; el saldo vive en la base y es de la cuenta, no de la marca.
      const [r, yo, saldo] = await Promise.all([api.estado(id, mesPedido), api.yo().catch(() => null), creditosDisponibles()]);
      setDatos({
        marca: r.brand,
        pasos: r.pasos,
        trabajos: r.trabajos,
        creditos: saldo?.disponibles ?? null,
        sinLimite: !!saldo?.sinLimite,
        precios: yo?.precios ?? null,
        mes: r.month ?? mesPedido,
        meses: r.meses ?? [],
      });
    } catch (e) {
      setError((e as Error).message);
    }
  }, [id, mesPedido]);

  useEffect(() => { void recargar(); }, [recargar]);
  // El lienzo se desplaza solo (el menú no se mueve): al cambiar de paso vuelve arriba.
  useEffect(() => { lienzo.current?.scrollTo({ top: 0 }); }, [pathname]);

  const hechos = GRUPOS.flatMap((g) => g.pasos).filter((p) => datos.pasos[p.id]?.status === 'approved').length;
  const siguiente = GRUPOS.flatMap((g) => g.pasos).find((p) => datos.pasos[p.id]?.status !== 'approved');
  // La sección es el primer tramo después de la marca (/marca/:id/video/sala → video).
  const ruta = pathname.split('/')[3] ?? '';
  const seccionActual = SECCIONES.find((x): x is Extract<Entrada, { nombre: string }> => 'nombre' in x && x.rutas.includes(ruta));
  const [cuentaAbierta, setCuentaAbierta] = useState(false);
  // El menú plegado a solo iconos se recuerda en este navegador.
  const [plegado, setPlegado] = useState(() => { try { return localStorage.getItem('igor-menu') === 'plegado'; } catch { return false; } });
  const plegar = (v: boolean) => { setPlegado(v); try { localStorage.setItem('igor-menu', v ? 'plegado' : 'abierto'); } catch { /* nada */ } };
  const total = GRUPOS.flatMap((g) => g.pasos).length;

  return (
    <Ctx.Provider value={{ marcaId: id, ...datos, recargar, cambiarMes }}>
      <div className={plegado ? 'app plegado' : 'app'}>
        <nav className="carril barra-lateral">
          <button className="plegar-tab" onClick={() => plegar(!plegado)} aria-label={plegado ? t('Abrir el menú') : t('Plegar el menú')} title={plegado ? t('Abrir el menú') : t('Plegar el menú')}>
            <span className={plegado ? '' : 'girar'}><Icono nombre="flecha" clase="ic-sm" /></span>
          </button>

          <div className="cab-menu">
            <Emblema id={id} nombre={datos.marca?.name ?? ''} logo={logoDeIdentidad(datos.pasos.estilo?.data)} clase="en-menu" />
            <span className="col nombre-marca" style={{ gap: 0, minWidth: 0 }}>
              <span className="saludo-menu">{t(saludo())} 👋</span>
              <span className="nombre">{datos.marca?.name ?? t('Cargando…')}</span>
            </span>
          </div>

          <hr className="linea-luz" />


          {SECCIONES.map((sec, i) => {
            if ('titulo' in sec) return sec.titulo ? <span key={i} className="grupo-menu texto-nav">{t(sec.titulo)}</span> : <span key={i} className="grupo-menu vacio" />;
            const pasosSec = sec.grupo?.pasos ?? [];
            const listos = pasosSec.filter((p) => datos.pasos[p.id]?.status === 'approved').length;
            // Tu marca y Tu mes llevan al primer paso que falta; si ya están todos, al primero (Tu marca, al resumen).
            // Tu marca, ya completa, lleva a su resumen de cuatro tarjetas.
            const falta = pasosSec.find((p) => datos.pasos[p.id]?.status !== 'approved');
            const destino = sec.grupo ? (falta ?? (sec.grupo === GRUPOS[0] ? { ruta: 'marca' } : pasosSec[0]!)).ruta : sec.ruta!;
            const activo = sec.rutas.includes(ruta);
            return (
              <NavLink key={sec.nombre} to={destino} className={activo ? 'paso-nav active' : 'paso-nav'} title={t(sec.nombre)}
                data-estado={pasosSec.length && listos === pasosSec.length ? 'listo' : undefined}>
                <span className="bolita"><Icono nombre={sec.icono} /></span>
                {/* En el celular el menú va en fila y el nombre largo la partía en dos: allí sale el corto. */}
                <span className="texto-nav">{sec.corto ? <><span className="nav-largo">{t(sec.nombre)}</span><span className="nav-corto">{t(sec.corto)}</span></> : t(sec.nombre)}</span>
                {pasosSec.length > 0 && <b className="num texto-nav">{listos}/{pasosSec.length}</b>}
              </NavLink>
            );
          })}

          <EstaSemana items={(datos.pasos.parrilla?.data as { items?: ItemDelMes[] } | undefined)?.items ?? []} ir={(r) => ir(`/marca/${id}/${r}`)} />

          <div className="menu-pie">
            <div className="abajo">
              <div className="yo">{iniciales(sesion?.user.email)}</div>
              <span className="quien texto-nav">{sesion?.user.email?.split('@')[0]}</span>
              <div className="menu-cuenta">
                <button className="plegar" aria-expanded={cuentaAbierta} aria-label={t('Tu cuenta')} title={t('Tu cuenta')} onClick={() => setCuentaAbierta(!cuentaAbierta)}>
                  <Icono nombre="menu" clase="ic-sm" />
                </button>
                {cuentaAbierta && (
                  <div className="menu-mes menu-cuenta-lista" role="menu" onClick={() => setCuentaAbierta(false)}>
                    <button role="menuitem" onClick={() => ir('/marcas')}><Icono nombre="marcas" clase="ic-sm" />{t('Tus marcas')}</button>
                    {SALIR.filter((x) => x.ruta === 'historial').map((x) => (
                      <button key={x.ruta} role="menuitem" onClick={() => ir(x.ruta)}><Icono nombre={x.icono} clase="ic-sm" />{t(x.nombre)}</button>
                    ))}
                    <button role="menuitem" onClick={() => { void supabase.auth.signOut({ scope: 'global' }); }}><Icono nombre="cerrar" clase="ic-sm" />{t('Salir')}</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </nav>

        <div className="lienzo" ref={lienzo}>
          <div className="barra-asis">
            <button className="volver" onClick={() => ir('/marcas')}>{t('‹ Tus marcas')}</button>
            <span style={{ fontWeight: 600 }}>{datos.marca?.name}</span>
            <span className="cual-paso">{t('{hechos} de {total} listos', { hechos, total })}</span>
            <div className="progreso" style={{ marginTop: 2 }}><i style={{ width: `${(hechos / total) * 100}%` }} /></div>
          </div>

          <header className="barra-sup">
            <nav className="miga" aria-label={t('Dónde estás')}>
              <button onClick={() => ir('/marcas')}>{datos.marca?.name ?? ''}</button>
              <span aria-hidden="true">›</span>
              <b>{t(seccionActual?.nombre ?? 'Inicio')}</b>
            </nav>
            <BotonCrear ir={(r) => ir(`/marca/${id}/${r}`)} irA={ir} />
            <SelectorDeMes mes={datos.mes} meses={datos.meses} cambiar={cambiarMes} />
            <SelectorDeIdioma />
            <BotonDeTema />
            <span className="sel-mes">
              <Icono nombre="creditos" />
              {datos.sinLimite ? t('sin límite') : datos.creditos === null ? '—' : t('{n} créditos', { n: datos.creditos })}
            </span>
          </header>

          {error && <p className="error">{error}</p>}
          {datos.sinLimite && <AvisoProveedores />}
          {/* Cada paso se baja al abrirlo: mientras llega, el menú y la barra siguen en su sitio. */}
          <Suspense fallback={<p className="apunte" style={{ padding: 24 }}>{t('Un momento…')}</p>}>
            <Outlet />
          </Suspense>
        </div>
      </div>
    </Ctx.Provider>
  );
}

const nombreMes = (m: string, locale: string) => {
  const t = new Date(`${m}-01T12:00:00Z`).toLocaleDateString(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' });
  return t[0]!.toUpperCase() + t.slice(1);
};

/** Los meses que se pueden planear: el actual y los tres siguientes. */
function mesesPlaneables(): string[] {
  const hoy = new Date();
  return [0, 1, 2, 3].map((n) => {
    const d = new Date(Date.UTC(hoy.getUTCFullYear(), hoy.getUTCMonth() + n, 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  });
}

/**
 * El mes que se está trabajando. Los meses con plan se abren; los que no, se ofrecen como "Planear …": al elegir
 * uno, el plan del mes aparece vacío para armarlo, y textos, diseños y videos siguen ese mes.
 */
function SelectorDeMes({ mes, meses, cambiar }: { mes: string | null; meses: string[]; cambiar: (m: string) => void }) {
  const [abierto, setAbierto] = useState(false);
  const ir = useNavigate();
  const { t, locale } = useIdioma();
  const nuevos = mesesPlaneables().filter((m) => !meses.includes(m));
  const elegir = (m: string, nuevo: boolean) => {
    cambiar(m);
    setAbierto(false);
    if (nuevo) ir('mes');
  };
  return (
    <div className="selector-mes">
      <button className="sel-mes boton" onClick={() => setAbierto(!abierto)} aria-expanded={abierto}>
        <Icono nombre="plan" />
        {mes ? nombreMes(mes, locale) : t('Elige un mes')}
        <span className={abierto ? 'girar-abajo abierto' : 'girar-abajo'}><Icono nombre="flecha" clase="ic-sm" /></span>
      </button>
      {abierto && (
        <div className="menu-mes" role="menu">
          {meses.length > 0 && <span className="menu-mes-titulo">{t('Tus meses')}</span>}
          {[...meses].reverse().map((m) => (
            <button key={m} role="menuitem" className={m === mes ? 'activo' : ''} onClick={() => elegir(m, false)}>
              <Icono nombre={m === mes ? 'check' : 'plan'} clase="ic-sm" />{nombreMes(m, locale)}
            </button>
          ))}
          {nuevos.length > 0 && <span className="menu-mes-titulo">{t('Planear un mes nuevo')}</span>}
          {nuevos.map((m) => (
            <button key={m} role="menuitem" className={m === mes ? 'activo nuevo' : 'nuevo'} onClick={() => elegir(m, true)}>
              <Icono nombre="mas" clase="ic-sm" />{nombreMes(m, locale)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Buen día, buenas tardes o buenas noches, según la hora. */
function saludo(): string {
  const h = new Date().getHours();
  return h < 12 ? 'Buen día' : h < 19 ? 'Buenas tardes' : 'Buenas noches';
}

/**
 * Solo para el equipo (cuentas sin límite): si un proveedor de IA se quedó sin saldo o le queda poco, un aviso rojo
 * arriba, antes de que los suscriptores vean fallar sus videos (30-09-2026: KIE se quedó en 0 y nadie lo supo).
 */
function AvisoProveedores() {
  const { t } = useIdioma();
  const [malos, setMalos] = useState<Array<{ proveedor: string; detalle: string | null; saldo_usd: number | null }>>([]);
  useEffect(() => { void api.estadoProveedores().then((r) => setMalos(r.proveedores.filter((x) => x.sin_saldo))).catch(() => {}); }, []);
  if (!malos.length) return null;
  return (
    <p className="aviso-proveedor" role="alert">
      <b>{t('Para el equipo:')}</b>{' '}
      {malos.map((m) => `${m.proveedor.toUpperCase()} — ${m.detalle ?? t('sin saldo')}${m.saldo_usd !== null ? ` (${Number(m.saldo_usd).toFixed(2)} USD)` : ''}`).join(' · ')}.{' '}
      {malos.some((m) => m.proveedor !== 'almacenamiento') && t('Recarga para que los videos y las imágenes no fallen.')}{' '}
      {malos.some((m) => m.proveedor === 'almacenamiento') && t('Sube el plan de Supabase antes de que se llene: si se llena, la app deja de guardar.')}
    </p>
  );
}

const mayuscula = (x: string) => (x ? x[0]!.toUpperCase() + x.slice(1) : x);
type ItemDelMes = { index: number; date: string; title?: string; format?: string };
const FORMATO_CORTO: Record<string, string> = { static: 'Imagen', carousel: 'Carrusel', story: 'Historia', reel: 'Video' };

/** Lo que sale esta semana, abajo del menú (06-10-2026, como los «Bookings» de la referencia del dueño). */
function EstaSemana({ items, ir }: { items: ItemDelMes[]; ir: (ruta: string) => void }) {
  const { t, locale } = useIdioma();
  const hoy = new Date().toISOString().slice(0, 10);
  const en7 = new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10);
  const lista = items.filter((i) => i.date >= hoy && i.date <= en7).sort((a, z) => a.date.localeCompare(z.date)).slice(0, 3);
  if (!lista.length) return null;
  return (
    <div className="esta-semana texto-nav">
      <span className="grupo-menu">{t('Esta semana')}</span>
      {lista.map((i) => (
        <button key={i.index} onClick={() => ir('publicar')}>
          <b>{mayuscula(new Date(`${i.date}T12:00:00Z`).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }))}</b>
          <span>{i.title ?? t(FORMATO_CORTO[i.format ?? ''] ?? 'Publicación')}</span>
        </button>
      ))}
    </div>
  );
}

/** «+ Crear»: lo que más se empieza, a un toque desde cualquier pantalla. */
function BotonCrear({ ir, irA }: { ir: (ruta: string) => void; irA: (ruta: string) => void }) {
  const { t } = useIdioma();
  const [abierto, setAbierto] = useState(false);
  useEffect(() => {
    if (!abierto) return;
    const cerrar = () => setAbierto(false);
    window.addEventListener('click', cerrar);
    return () => window.removeEventListener('click', cerrar);
  }, [abierto]);
  const opciones: Array<{ texto: string; icono: NombreDeIcono; hacer: () => void }> = [
    { texto: 'Un video', icono: 'videos', hacer: () => ir('video/crear') },
    { texto: 'Los diseños del mes', icono: 'disenos', hacer: () => ir('disenos') },
    { texto: 'El plan de otro mes', icono: 'plan', hacer: () => ir('mes') },
    { texto: 'Otra marca', icono: 'marcas', hacer: () => irA('/marcas') },
  ];
  return (
    <div className="crear-menu" onClick={(e) => e.stopPropagation()}>
      <button className="btn-crear" aria-expanded={abierto} onClick={() => setAbierto(!abierto)}><Icono nombre="mas" clase="ic-sm" /> {t('Crear')}</button>
      {abierto && (
        <div className="menu-mes crear-lista" role="menu">
          {opciones.map((o) => (
            <button key={o.texto} role="menuitem" onClick={() => { setAbierto(false); o.hacer(); }}><Icono nombre={o.icono} clase="ic-sm" />{t(o.texto)}</button>
          ))}
        </div>
      )}
    </div>
  );
}
